import { useCallback } from 'react';
import { createNewSearch, fetchFilteredProvidersForSearch } from '../utils/search-creation.utils';
import { callMatchmakingApi, handleNoResultsResponse, extractMatchResults } from '../utils/matchmaking-api.utils';
import { processSearchResults } from '../utils/result-processing.utils';
import { GENERIC_SEARCH_ID } from '../constants/client-selection';
import type { StepperAnswers } from './useMatchmakingStateManagement';

type MatchmakingState = ReturnType<typeof import('./useMatchmakingState').useMatchmakingState>;

/**
 * Feature 060: Removed miles and selectedServiceRanges parameters
 */
type UseMatchSearchProps = {
  matchingPrompt: string;
  hasClients: boolean;
  selectedClientId: string | null;
  advisorProfileId: string | null;
  stepperAnswers: StepperAnswers;
  apiResults: any[];
  currentSearchId: number | null;
  loadCount: number;
  selectedState: string;
  selectedCounty: string;
  selectedAddress: string;
  selectedZipCode: string;
  selectedLatitude: number | null;
  selectedLongitude: number | null;
  clientProfile: {
    relationship?: string | null;
    reasonForJoining?: string | null;
    ageRange?: string | null;
  } | undefined;
  userType?: 'CARE_SEEKER' | 'FINANCIAL_ADVISOR' | 'SERVICE_PROVIDER'; // FEATURE 025, 036: Routes searches for CS, FA, and SP
  matchmakingState: MatchmakingState;
  isViewingHistoryResults: boolean;
  createSearchMutation: any;
  filteredProvidersMutation: any;
  saveResultsMutation: any;
  setIsLoading: (value: boolean) => void;
  setIsLoadingMore: (value: boolean) => void;
  setError: (error: string | null) => void;
  setShowNoProvidersFound: (value: boolean) => void;
  setNoProviderMessage: (message: string | null) => void;
  setLoadMoreMessage: (message: string | null) => void;
  setIsLocationModalOpen: (value: boolean) => void;
  setCurrentSearchId: (id: number | null) => void;
  setLoadCount: (count: number) => void;
  setSearchCounter: (updater: (prev: number) => number) => void;
  setIsViewingHistoryResults: (value: boolean) => void;
  setShouldFetchHistory: (value: boolean) => void;
  lastPromptRef: React.MutableRefObject<string>;
};

 // Hook for executing matchmaking searches. Handles both new searches and load more operations. Manages the complete search flow from creation to result display.

export const useMatchSearch = ({
  matchingPrompt,
  hasClients,
  selectedClientId,
  advisorProfileId,
  stepperAnswers,
  apiResults,
  currentSearchId,
  loadCount,
  selectedState,
  selectedCounty,
  selectedAddress,
  selectedZipCode,
  selectedLatitude,
  selectedLongitude,
  clientProfile,
  userType, // FEATURE 025: Pass through for API routing
  matchmakingState,
  isViewingHistoryResults,
  createSearchMutation,
  filteredProvidersMutation,
  saveResultsMutation,
  setIsLoading,
  setIsLoadingMore,
  setError,
  setShowNoProvidersFound,
  setNoProviderMessage,
  setLoadMoreMessage,
  setIsLocationModalOpen,
  setCurrentSearchId,
  setLoadCount,
  setSearchCounter,
  setIsViewingHistoryResults,
  setShouldFetchHistory,
  lastPromptRef,
}: UseMatchSearchProps) => {
  const executeMatchSearch = useCallback(
    async (isLoadMore = false, answers?: StepperAnswers) => {
      // FEATURE 021: Allow searches without a specific client (generic search mode)
      const isGenericSearch = selectedClientId === GENERIC_SEARCH_ID;
      if (!matchingPrompt.trim() || !hasClients || (!selectedClientId && !isGenericSearch) || !advisorProfileId) {
        return;
      }

      const answersToUse = answers || stepperAnswers;
      const currentMatches = apiResults.length;
      const isLoadingState = isLoadMore ? setIsLoadingMore : setIsLoading;
      isLoadingState(true);
      setError(null);
      setShowNoProvidersFound(false);
      setNoProviderMessage(null);
      setLoadMoreMessage(null);
      setIsLocationModalOpen(false);

      try {
        let filterValues: string | null = null;
        let searchId: number | null = isLoadMore ? currentSearchId : null;

        if (!isLoadMore && advisorProfileId && selectedClientId) {
          try {
            const searchResult = await createNewSearch({
              advisorProfileId,
              selectedClientId,
              matchingPrompt,
              selectedState,
              selectedCounty,
              selectedAddress,
              selectedZipCode,
              selectedLatitude,
              selectedLongitude,
              clientProfile,
              answersToUse,
              userType, // FEATURE 025: Pass through for API routing
              createSearchMutation,
              filteredProvidersMutation,
            });

            searchId = searchResult.searchId;
            setCurrentSearchId(searchResult.searchId);
            setLoadCount(1);

            if (searchResult.providersFound === 0) {
              setShowNoProvidersFound(true);
              setNoProviderMessage('Looks like this search did not return any providers.');
              if (selectedClientId) {
                matchmakingState.setClientQuery(lastPromptRef.current);
              }
              isLoadingState(false);
              return;
            }

            filterValues = searchResult.filterValues;

            if (!filterValues) {
              setShowNoProvidersFound(true);
              setNoProviderMessage('No providers available for the selected filters.');
              isLoadingState(false);
              return;
            }
          } catch (searchError: any) {
            const errorMessage =
              searchError?.response?.data?.message ||
              searchError?.message ||
              'Failed to create matchmaking search. Please try again.';

            if (errorMessage.toLowerCase().includes('no providers qualified')) {
              setShowNoProvidersFound(true);
              setNoProviderMessage(errorMessage);
            } else {
              setError(errorMessage);
            }
            isLoadingState(false);
            return;
          }
        } else if (isLoadMore && !currentSearchId) {
          setError('Unable to load more results. Please start a new search.');
          isLoadingState(false);
          return;
        } else if (isLoadMore && currentSearchId && advisorProfileId && selectedClientId) {
          try {
            filterValues = await fetchFilteredProvidersForSearch({
              currentSearchId,
              advisorProfileId,
              selectedClientId,
              filteredProvidersMutation,
            });

            if (!filterValues) {
              matchmakingState.setClientMatches({
                matches: apiResults,
                append: false,
                hasMore: false,
              });
              isLoadingState(false);
              return;
            }
          } catch (filteredProvidersError) {
            console.error('Error fetching filtered providers:', filteredProvidersError);
            setError('Failed to load more results. Please try again.');
            isLoadingState(false);
            return;
          }
        }

        const matchmakingApiData = await callMatchmakingApi({
          matchingPrompt,
          filterValues,
          answersToUse,
        });

        const matchmakingStatusCode = matchmakingApiData.status_code ?? 200;

        if (matchmakingStatusCode === 204) {
          const apiMessage = handleNoResultsResponse(matchmakingApiData);
          if (isLoadMore) {
            setLoadMoreMessage(apiMessage);
            matchmakingState.setClientMatches({
              matches: apiResults,
              append: false,
              hasMore: false,
            });
          } else {
            setShowNoProvidersFound(true);
            setNoProviderMessage(apiMessage);
            matchmakingState.setClientQuery(lastPromptRef.current);
          }
          isLoadingState(false);
          return;
        }

        const matchResultsRecord = extractMatchResults(matchmakingApiData);
        await processSearchResults({
          matchmakingApiData,
          matchResultsRecord,
          searchId,
          advisorProfileId,
          selectedClientId,
          loadCount,
          isLoadMore,
          currentMatches,
          matchmakingState,
          saveResultsMutation,
          setLoadCount,
          setError,
          setSearchCounter,
          setIsViewingHistoryResults,
          setShouldFetchHistory,
        });
      } catch (err) {
        if (!isLoadMore) {
          matchmakingState.setClientMatches({
            matches: [],
            append: false,
            hasMore: false,
          });
        }
        if (selectedClientId) {
          matchmakingState.setClientQuery(lastPromptRef.current);
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch matches. Please try again.',
        );
      } finally {
        isLoadingState(false);
      }
    },
    [
      matchingPrompt,
      hasClients,
      selectedClientId,
      advisorProfileId,
      stepperAnswers,
      apiResults,
      currentSearchId,
      loadCount,
      selectedState,
      selectedCounty,
      selectedAddress,
      selectedZipCode,
      selectedLatitude,
      selectedLongitude,
      clientProfile,
      userType, // FEATURE 025: Include in dependencies
      matchmakingState,
      isViewingHistoryResults,
      createSearchMutation,
      filteredProvidersMutation,
      saveResultsMutation,
      setIsLoading,
      setIsLoadingMore,
      setError,
      setShowNoProvidersFound,
      setNoProviderMessage,
      setLoadMoreMessage,
      setIsLocationModalOpen,
      setCurrentSearchId,
      setLoadCount,
      setSearchCounter,
      setIsViewingHistoryResults,
      setShouldFetchHistory,
      lastPromptRef,
    ],
  );
  return { executeMatchSearch };
};
